"use client";

import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { Table } from "@package/subframe/components/Table";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherArchive,
  FeatherCheck,
  FeatherCopy,
  FeatherMoreHorizontal,
  FeatherRotateCcw,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { useGlobalTemplates } from "@/api/hooks/useTemplates";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import { useFindOrganisationQueryControllerFindBySlug } from "@/generated/organisations/organisations";
import {
  useArchiveTemplateCommandControllerArchiveTemplate,
  useCopyTemplateToOrganisationCommandControllerCopyTemplateToOrganisation,
  useGetTemplatesByOrganisationQueryControllerGetTemplatesByOrganisation,
  usePublishTemplateCommandControllerPublishTemplate,
} from "@/generated/questionnaire-templates/questionnaire-templates";
import { clampPage, DEFAULT_PAGE_SIZE, totalPages } from "@/lib/pagination";

export const Route = createFileRoute(
  "/_authenticated/organisations/$slug/templates",
)({
  component: OrganisationTemplatesPage,
});

interface OrgTemplate {
  id: string;
  organisationId: string | null;
  sourceId: string | null;
  name: string;
  description: string | null;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  version: number;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  questions: { id: string; questionText: string }[];
}

interface ConfirmDialogState {
  open: boolean;
  type: "copy" | "archive" | "publish";
  templateId: string;
  templateName: string;
}

interface OrgTemplatesSectionProps {
  templateList: OrgTemplate[];
  title: string;
  titleClassName?: string;
  getStatusBadge: (status: OrgTemplate["status"]) => React.ReactNode;
  onPublish: (templateId: string, templateName: string) => void;
  onArchive: (templateId: string, templateName: string) => void;
  isMutating: boolean;
}

function OrgTemplatesSection({
  templateList,
  title,
  titleClassName,
  getStatusBadge,
  onPublish,
  onArchive,
  isMutating,
}: OrgTemplatesSectionProps) {
  const [page, setPage] = useState(1); // 1-based, matches the Pagination component
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // The endpoint returns every template for the organisation, so each status
  // section slices its own page client-side. Archiving or publishing moves a
  // row to another section, which would otherwise leave `page` past the end.
  const safePage = clampPage(page, totalPages(templateList.length, pageSize));
  const pagedTemplates = useMemo(
    () => templateList.slice((safePage - 1) * pageSize, safePage * pageSize),
    [templateList, safePage, pageSize],
  );

  if (templateList.length === 0) return null;

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <span
        className={`text-heading-3 font-heading-3 ${titleClassName || "text-default-font"}`}
      >
        {title}
      </span>
      <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
        <Table
          className={TABLE_MIN_WIDTH.md}
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Template name</Table.HeaderCell>
              <Table.HeaderCell>Description</Table.HeaderCell>
              <Table.HeaderCell>Questions</Table.HeaderCell>
              <Table.HeaderCell>Version</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell className="items-center justify-end">
                Actions
              </Table.HeaderCell>
            </Table.HeaderRow>
          }
        >
          {pagedTemplates.map((template) => {
            const isArchived = template.status === "ARCHIVED";

            return (
              <Table.Row
                key={template.id}
                className={isArchived ? "opacity-60" : ""}
              >
                <Table.Cell>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {template.name}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-subtext-color line-clamp-1">
                    {template.description || "No description"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {template.questions?.length || 0}{" "}
                    {(template.questions?.length || 0) === 1
                      ? "question"
                      : "questions"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-default-font">
                    {template.version}
                  </span>
                </Table.Cell>
                <Table.Cell>{getStatusBadge(template.status)}</Table.Cell>
                <Table.Cell variant="aligned-right">
                  <div className="flex items-center justify-end gap-2">
                    <SubframeCore.DropdownMenu.Root>
                      <SubframeCore.DropdownMenu.Trigger asChild={true}>
                        <IconButton
                          variant="neutral-primary"
                          size="medium"
                          icon={<FeatherMoreHorizontal />}
                        />
                      </SubframeCore.DropdownMenu.Trigger>
                      <SubframeCore.DropdownMenu.Portal>
                        <SubframeCore.DropdownMenu.Content
                          side="bottom"
                          align="end"
                          sideOffset={4}
                          asChild={true}
                        >
                          <DropdownMenu>
                            {isArchived ? (
                              <DropdownMenu.DropdownItem
                                icon={<FeatherRotateCcw />}
                                onClick={() =>
                                  onPublish(template.id, template.name)
                                }
                                disabled={isMutating}
                              >
                                Restore & Publish
                              </DropdownMenu.DropdownItem>
                            ) : (
                              <>
                                {template.status === "DRAFT" && (
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherCheck />}
                                    onClick={() =>
                                      onPublish(template.id, template.name)
                                    }
                                    disabled={isMutating}
                                  >
                                    Publish
                                  </DropdownMenu.DropdownItem>
                                )}
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherArchive />}
                                  onClick={() =>
                                    onArchive(template.id, template.name)
                                  }
                                  disabled={isMutating}
                                >
                                  Archive
                                </DropdownMenu.DropdownItem>
                              </>
                            )}
                          </DropdownMenu>
                        </SubframeCore.DropdownMenu.Content>
                      </SubframeCore.DropdownMenu.Portal>
                    </SubframeCore.DropdownMenu.Root>
                  </div>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table>

        <Pagination
          className="pt-4"
          page={safePage}
          pageSize={pageSize}
          totalCount={templateList.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          itemNoun="templates"
        />
      </div>
    </div>
  );
}

function OrganisationTemplatesPage() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    type: "copy",
    templateId: "",
    templateName: "",
  });
  const [copyName, setCopyName] = useState("");

  // Fetch organisation by slug to get the ID
  const { data: orgData, isLoading: orgLoading } =
    useFindOrganisationQueryControllerFindBySlug(slug);
  const organisation = orgData?.data;
  const organisationId = organisation?.id;

  // Fetch organisation templates
  const {
    data: orgTemplatesData,
    isLoading: orgTemplatesLoading,
    refetch: refetchOrgTemplates,
  } = useGetTemplatesByOrganisationQueryControllerGetTemplatesByOrganisation(
    { organisationId: organisationId },
    { query: { enabled: !!organisationId } },
  );
  const orgTemplates = ((orgTemplatesData as unknown as { data: OrgTemplate[] })
    ?.data || []) as OrgTemplate[];

  // Fetch all published global templates
  const { data: globalTemplates, isLoading: globalLoading } =
    useGlobalTemplates("PUBLISHED");

  // Mutations
  const copyMutation =
    useCopyTemplateToOrganisationCommandControllerCopyTemplateToOrganisation();
  const archiveMutation = useArchiveTemplateCommandControllerArchiveTemplate();
  const publishMutation = usePublishTemplateCommandControllerPublishTemplate();

  const isLoading = orgLoading || orgTemplatesLoading || globalLoading;

  // Group org templates by status
  const publishedOrgTemplates = orgTemplates.filter(
    (t) => t.status === "PUBLISHED",
  );
  const draftOrgTemplates = orgTemplates.filter((t) => t.status === "DRAFT");
  const archivedOrgTemplates = orgTemplates.filter(
    (t) => t.status === "ARCHIVED",
  );

  const handleCopyClick = (templateId: string, templateName: string) => {
    setCopyName(templateName);
    setConfirmDialog({
      open: true,
      type: "copy",
      templateId,
      templateName,
    });
  };

  const handleArchiveClick = (templateId: string, templateName: string) => {
    setConfirmDialog({
      open: true,
      type: "archive",
      templateId,
      templateName,
    });
  };

  const handlePublishClick = (templateId: string, templateName: string) => {
    setConfirmDialog({
      open: true,
      type: "publish",
      templateId,
      templateName,
    });
  };

  const invalidateTemplatesCache = () => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === "/v1/questionnaire-templates",
      refetchType: "all",
    });
  };

  const handleConfirm = () => {
    if (confirmDialog.type === "copy") {
      copyMutation.mutate(
        {
          templateId: confirmDialog.templateId,
          data: {
            organisationId: organisationId!,
            name: copyName.trim() || undefined,
          },
        },
        {
          onSuccess: () => {
            toast.success(`"${copyName}" copied to nursery group`);
            refetchOrgTemplates();
            invalidateTemplatesCache();
            setConfirmDialog((prev) => ({ ...prev, open: false }));
            setCopyName("");
          },
          onError: () => {
            toast.error("Failed to copy template");
          },
        },
      );
    } else if (confirmDialog.type === "archive") {
      archiveMutation.mutate(
        { templateId: confirmDialog.templateId },
        {
          onSuccess: () => {
            toast.success(`"${confirmDialog.templateName}" archived`);
            refetchOrgTemplates();
            invalidateTemplatesCache();
            setConfirmDialog((prev) => ({ ...prev, open: false }));
          },
          onError: () => {
            toast.error("Failed to archive template");
          },
        },
      );
    } else if (confirmDialog.type === "publish") {
      publishMutation.mutate(
        { templateId: confirmDialog.templateId },
        {
          onSuccess: () => {
            toast.success(`"${confirmDialog.templateName}" published`);
            refetchOrgTemplates();
            invalidateTemplatesCache();
            setConfirmDialog((prev) => ({ ...prev, open: false }));
          },
          onError: () => {
            toast.error("Failed to publish template");
          },
        },
      );
    }
  };

  const isMutating =
    copyMutation.isPending ||
    archiveMutation.isPending ||
    publishMutation.isPending;

  const getStatusBadge = (status: OrgTemplate["status"]) => {
    switch (status) {
      case "PUBLISHED":
        return <Badge variant="success">Published</Badge>;
      case "DRAFT":
        return <Badge variant="warning">Draft</Badge>;
      case "ARCHIVED":
        return <Badge variant="neutral">Archived</Badge>;
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-8 bg-white animate-fade-in">
      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Spinner className="h-8 w-8 text-primary" />
        </div>
      ) : (
        <>
          {/* Organisation Templates Section */}
          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex w-full flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Nursery Group Templates - {organisation?.name}
              </span>
              <span className="text-body font-body text-subtext-color">
                Templates owned by this nursery group
              </span>
            </div>

            {orgTemplates.length > 0 ? (
              <div className="flex w-full flex-col items-start gap-8">
                <OrgTemplatesSection
                  templateList={publishedOrgTemplates}
                  title="Published"
                  getStatusBadge={getStatusBadge}
                  onPublish={handlePublishClick}
                  onArchive={handleArchiveClick}
                  isMutating={isMutating}
                />
                <OrgTemplatesSection
                  templateList={draftOrgTemplates}
                  title="Drafts"
                  getStatusBadge={getStatusBadge}
                  onPublish={handlePublishClick}
                  onArchive={handleArchiveClick}
                  isMutating={isMutating}
                />
                <OrgTemplatesSection
                  templateList={archivedOrgTemplates}
                  title="Archived"
                  titleClassName="text-subtext-color"
                  getStatusBadge={getStatusBadge}
                  onPublish={handlePublishClick}
                  onArchive={handleArchiveClick}
                  isMutating={isMutating}
                />
              </div>
            ) : (
              <div className="flex w-full flex-col items-center justify-center py-12 gap-4 rounded-md border border-solid border-secondary-beige-200">
                <IconWithLogoBackground variant="neutral" size="x-large" />
                <div className="flex flex-col items-center gap-2">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    No nursery group templates
                  </span>
                  <span className="text-body font-body text-subtext-color text-center max-w-md">
                    Copy a global template below to create a nursery group
                    template that you can customise.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Available Global Templates Section */}
          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex w-full flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Available Global Templates
              </span>
              <span className="text-body font-body text-subtext-color">
                Copy a global template to create an nursery group-specific
                version
              </span>
            </div>

            {globalTemplates && globalTemplates.length > 0 ? (
              <div className="flex w-full flex-col items-start bg-default-background overflow-auto">
                <Table
                  className={TABLE_MIN_WIDTH.md}
                  header={
                    <Table.HeaderRow>
                      <Table.HeaderCell>Template name</Table.HeaderCell>
                      <Table.HeaderCell>Description</Table.HeaderCell>
                      <Table.HeaderCell>Questions</Table.HeaderCell>
                      <Table.HeaderCell>Version</Table.HeaderCell>
                      <Table.HeaderCell className="items-center justify-end">
                        Actions
                      </Table.HeaderCell>
                    </Table.HeaderRow>
                  }
                >
                  {globalTemplates.map((template) => (
                    <Table.Row key={template.id}>
                      <Table.Cell>
                        <span className="text-body-bold font-body-bold text-default-font">
                          {template.name}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-subtext-color line-clamp-1">
                          {template.description || "No description"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-default-font">
                          {template.items?.length || 0}{" "}
                          {(template.items?.length || 0) === 1
                            ? "question"
                            : "questions"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-default-font">
                          {template.version}
                        </span>
                      </Table.Cell>
                      <Table.Cell variant="aligned-right">
                        <Button
                          variant="neutral-secondary"
                          size="medium"
                          icon={<FeatherCopy />}
                          onClick={() =>
                            handleCopyClick(template.id, template.name)
                          }
                          disabled={isMutating}
                        >
                          Copy
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table>
              </div>
            ) : (
              <div className="flex w-full flex-col items-center justify-center py-12 gap-4 rounded-md border border-solid border-secondary-beige-200">
                <IconWithLogoBackground variant="neutral" size="x-large" />
                <div className="flex flex-col items-center gap-2">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    No global templates available
                  </span>
                  <span className="text-body font-body text-subtext-color text-center max-w-md">
                    There are no published global templates to copy.
                  </span>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Confirmation Dialogs */}
      {/* Copy Dialog */}
      <Dialog
        open={confirmDialog.open && confirmDialog.type === "copy"}
        onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <DialogTitle className="sr-only">Copy template</DialogTitle>
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
            <div className="flex w-full flex-col items-center gap-6 p-6">
              <div className="flex flex-col items-center gap-6">
                <IconWithLogoBackground
                  variant="brand"
                  size="x-large"
                  icon={<FeatherCopy />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center">
                    Copy Template
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    Copy <strong>{confirmDialog.templateName}</strong> to this
                    nursery group. You can customise the name below.
                  </span>
                </div>
              </div>
              <TextField
                className="h-auto w-full"
                size="large"
                label="Template name"
              >
                <TextField.Input
                  placeholder="Enter template name"
                  value={copyName}
                  onChange={(e) => setCopyName(e.target.value)}
                  disabled={isMutating}
                />
              </TextField>
            </div>
            <div className="flex w-full flex-col items-center gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
              <Button
                className="h-12 w-full"
                variant="brand-primary"
                size="large"
                onClick={handleConfirm}
                disabled={isMutating || !copyName.trim()}
              >
                {isMutating ? "Copying..." : "Copy Template"}
              </Button>
              <Button
                className="h-12 w-full"
                variant="neutral-tertiary"
                size="large"
                onClick={() =>
                  setConfirmDialog((prev) => ({ ...prev, open: false }))
                }
                disabled={isMutating}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog
        open={confirmDialog.open && confirmDialog.type === "archive"}
        onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <DialogTitle className="sr-only">Archive template</DialogTitle>
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
              <div className="flex flex-col items-center gap-6">
                <IconWithLogoBackground
                  variant="warning"
                  size="x-large"
                  icon={<FeatherArchive />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center">
                    Archive template?
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    Are you sure you want to archive{" "}
                    <strong>{confirmDialog.templateName}</strong>? Archived
                    templates cannot be used for new questionnaire configs.
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
              <Button
                className="h-14 w-80 flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={() =>
                  setConfirmDialog((prev) => ({ ...prev, open: false }))
                }
                disabled={isMutating}
              >
                Cancel
              </Button>
              <Button
                className="h-14 w-80 flex-none"
                variant="destructive-primary"
                size="extra-large"
                onClick={handleConfirm}
                disabled={isMutating}
              >
                {isMutating ? "Archiving..." : "Yes, archive"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog
        open={confirmDialog.open && confirmDialog.type === "publish"}
        onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <DialogTitle className="sr-only">Publish template</DialogTitle>
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
              <div className="flex flex-col items-center gap-6">
                <IconWithLogoBackground
                  variant="success"
                  size="x-large"
                  icon={<FeatherCheck />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center">
                    Publish template?
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    Are you sure you want to publish{" "}
                    <strong>{confirmDialog.templateName}</strong>? Published
                    templates can be used by branches in this nursery group.
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
              <Button
                className="h-14 w-80 flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={() =>
                  setConfirmDialog((prev) => ({ ...prev, open: false }))
                }
                disabled={isMutating}
              >
                Cancel
              </Button>
              <Button
                className="h-14 w-80 flex-none"
                variant="brand-primary"
                size="extra-large"
                onClick={handleConfirm}
                disabled={isMutating}
              >
                {isMutating ? "Publishing..." : "Yes, publish"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
